import type { ClientAnalyticsPageData } from "./analytics.js";

const INVALID_URL = "<invalid>";
const REDACTED_QUERY_PARAM_VALUE = "[redacted]";
const RELATIVE_URL_BASE = "https://example.com";

/**
 * Rudderstack's sentinel for a session that started from direct navigation
 * (no document.referrer). Matches the value the SDK previously reported.
 */
export const DIRECT_REFERRER = "$direct";

// Bounds recursion into URL-valued params like `redirectTo`; anything nested
// deeper is redacted wholesale.
const MAX_NESTED_URL_DEPTH = 3;

const URL_SCHEME_REGEX = /^[a-z][a-z0-9+.-]*:\/\//i;
const URL_IN_TEXT_REGEX = /\b(?:https?|wss?):\/\/[^\s"'<>()[\]{}]+/gi;

// Suffix matching to hopefully catch future leaks with minimal false positives.
const SENSITIVE_QUERY_PARAM_SUFFIXES = [
  "token",
  "secret",
  "signature",
  "password",
];

// Exact-match credential params: OWASP/OAuth-standard names or params we
// actually issue
// `key` `code` and `state` are exact matched to avoid flagging e.g. sortKey, promoCode, statement
const SENSITIVE_QUERY_PARAM_KEYS = new Set([
  "apikey",
  "authorizationcode",
  "code",
  "jwt",
  "otp",
  "sig",
  "state",
  "usercode",
]);

export function sanitizeUrlForAnalytics(urlString: string): string {
  return sanitizeUrlAtDepth(urlString, 0);
}

/**
 * Replaces every absolute URL embedded in free text (e.g. Datadog log messages
 * and error stack traces) with its sanitized form.
 */
export function sanitizeUrlsInText(text: string): string {
  if (!text.includes("://")) {
    return text;
  }
  return text.replace(URL_IN_TEXT_REGEX, (match) =>
    sanitizeUrlForAnalytics(match),
  );
}

type ClientAnalyticsPageDataInput = Omit<
  ClientAnalyticsPageData,
  "initial_referring_domain" | "referring_domain"
>;

/**
 * Referring domains are derived from the sanitized referrer URLs, so callers
 * collecting browser page data may omit them.
 */
export function sanitizeClientAnalyticsPageData(
  page: ClientAnalyticsPageDataInput,
): ClientAnalyticsPageData {
  const referrer = sanitizeReferrer(page.referrer);
  const initialReferrer = sanitizeReferrer(page.initial_referrer);

  return {
    ...page,
    path: sanitizeUrlForAnalytics(page.path),
    url: sanitizeUrlForAnalytics(page.url),
    tab_url: sanitizeUrlForAnalytics(page.tab_url),
    search: sanitizeUrlForAnalytics(page.search),
    referrer,
    referring_domain: getUrlHostname(referrer),
    initial_referrer: initialReferrer,
    initial_referring_domain: getUrlHostname(initialReferrer),
  };
}

function sanitizeUrlAtDepth(urlString: string, depth: number): string {
  if (urlString === "") {
    return "";
  }

  const inputKind = urlString.startsWith("?")
    ? "query"
    : urlString.startsWith("/") && !urlString.startsWith("//")
      ? "path"
      : "absolute";

  let url: URL;
  try {
    url =
      inputKind === "absolute"
        ? new URL(urlString)
        : new URL(urlString, RELATIVE_URL_BASE);
  } catch {
    return INVALID_URL;
  }

  url.username = "";
  url.password = "";
  url.hash = "";

  for (const key of new Set(url.searchParams.keys())) {
    if (key.startsWith("_")) {
      url.searchParams.delete(key);
    } else if (isSensitiveQueryParam(key)) {
      url.searchParams.set(key, REDACTED_QUERY_PARAM_VALUE);
    } else {
      sanitizeUrlValuedParam(url, key, depth);
    }
  }

  if (inputKind === "query") {
    return url.search;
  }
  if (inputKind === "path") {
    return `${url.pathname}${url.search}`;
  }
  return url.toString();
}

/**
 * Params like `redirectTo` can carry a whole URL whose own query string may
 * contain credentials, so URL-shaped values are sanitized recursively.
 */
function sanitizeUrlValuedParam(url: URL, key: string, depth: number): void {
  const values = url.searchParams.getAll(key);
  const sanitizedValues = values.map((value) => {
    if (!isUrlLikeValue(value)) {
      return value;
    }
    return depth >= MAX_NESTED_URL_DEPTH
      ? REDACTED_QUERY_PARAM_VALUE
      : sanitizeUrlAtDepth(value, depth + 1);
  });

  if (sanitizedValues.every((value, index) => value === values[index])) {
    return;
  }

  const [firstValue] = sanitizedValues;
  if (sanitizedValues.length === 1 && firstValue != null) {
    url.searchParams.set(key, firstValue);
  } else {
    url.searchParams.delete(key);
    for (const value of sanitizedValues) {
      url.searchParams.append(key, value);
    }
  }
}

function isUrlLikeValue(value: string): boolean {
  return (
    value.startsWith("/") ||
    value.startsWith("?") ||
    URL_SCHEME_REGEX.test(value)
  );
}

function isSensitiveQueryParam(key: string): boolean {
  const normalizedKey = key
    .toLowerCase()
    .replaceAll("-", "")
    .replaceAll("_", "");
  if (SENSITIVE_QUERY_PARAM_KEYS.has(normalizedKey)) {
    return true;
  }

  if (
    SENSITIVE_QUERY_PARAM_SUFFIXES.some((suffix) =>
      normalizedKey.endsWith(suffix),
    )
  ) {
    return true;
  }
  return false;
}

function sanitizeReferrer(referrer: string): string {
  return referrer === DIRECT_REFERRER
    ? DIRECT_REFERRER
    : sanitizeUrlForAnalytics(referrer);
}

function getUrlHostname(urlString: string): string {
  if (
    urlString === "" ||
    urlString === DIRECT_REFERRER ||
    urlString === INVALID_URL
  ) {
    return "";
  }

  try {
    return new URL(urlString).hostname;
  } catch {
    return "";
  }
}
